/**
 * pdfFirstPage - render page 1 of a user-uploaded PDF to a PNG
 * data URL, for the Document Editor's personal refresh surface
 * (2026-11-05).
 *
 * This is a RENDERER, nothing more: it rasterizes the first page
 * so the page can serve as the background of the user's own
 * refresh workspace. It extracts no text, runs no OCR, detects
 * nothing, and never touches the protected clone engines - the
 * rendered page is a picture the user edits on top of by hand.
 *
 * pdf.js is loaded lazily (dynamic import) so the editor's module
 * graph - and every test that renders the editor - never pulls it
 * in. The worker is the vendored build in public/, pinned to the
 * pdfjs-dist version in package.json.
 */

export interface PdfFirstPageResult {
    /** PNG data URL of page 1. */
    dataUrl: string;
    /** Pixel size of the rendered raster. */
    width: number;
    height: number;
    pageCount: number;
}

/** Long edge the page raster is rendered at before the refresh
 * ingest downscales it for storage (REFRESH_INGEST_MAX_DIM). A
 * generous render keeps the stored page crisp. */
export const PDF_RENDER_MAX_DIM = 2400;

let workerConfigured = false;

async function loadPdfJs(): Promise<any> {
    const pdfjs: any = await import('pdfjs-dist/legacy/build/pdf');
    if (!workerConfigured) {
        workerConfigured = true;
        try {
            pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.js`;
        } catch { /* worker setup is best-effort; a failure surfaces at getDocument */ }
    }
    return pdfjs;
}

function readAsArrayBuffer(file: Blob): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result as ArrayBuffer);
        r.onerror = () => reject(r.error || new Error('Could not read the file'));
        r.readAsArrayBuffer(file);
    });
}

/**
 * Render the first page of a PDF file. Throws on anything that is
 * not a readable PDF - the caller reports the failure honestly
 * and keeps the user's current document untouched.
 */
export async function renderPdfFirstPage(
    file: File,
    opts: { createCanvas?: (w: number, h: number) => HTMLCanvasElement; pdfjs?: any } = {}
): Promise<PdfFirstPageResult> {
    const pdfjs = opts.pdfjs || (await loadPdfJs());
    const data = await readAsArrayBuffer(file);
    const pdf = await pdfjs.getDocument({ data }).promise;
    try {
        const page = await pdf.getPage(1);
        const base = page.getViewport({ scale: 1 });
        const scale = PDF_RENDER_MAX_DIM / Math.max(base.width, base.height);
        const viewport = page.getViewport({ scale: Math.max(0.1, scale) });
        const width = Math.max(1, Math.floor(viewport.width));
        const height = Math.max(1, Math.floor(viewport.height));
        const canvas = opts.createCanvas
            ? opts.createCanvas(width, height)
            : (() => { const c = document.createElement('canvas'); c.width = width; c.height = height; return c; })();
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Canvas is not available in this browser');
        // Pages can carry transparency; the refresh background is a
        // sheet of paper, so render onto white.
        (ctx as any).fillStyle = '#FFFFFF';
        (ctx as any).fillRect(0, 0, width, height);
        await page.render({ canvasContext: ctx, viewport }).promise;
        return { dataUrl: canvas.toDataURL('image/png'), width, height, pageCount: pdf.numPages };
    } finally {
        try { await pdf.destroy(); } catch { /* already gone */ }
    }
}
